import * as React from 'react'
import { ReactElement, useState } from 'react'
import { AccordionHeader, AccordionPanel, Button, mergeClasses, Text } from '@fluentui/react-components'
import { FieldArray, useFormikContext } from 'formik'
import { Tree, TreeItem, TreeItemPersonaLayout } from '@fluentui/react-components/unstable'
import { FamilyLawModuleDto, OffspringDto } from '../../../../hooks/module/useFamilyLaw'
import { AccessibilityRegular, AddCircleRegular, DeleteRegular, HomeRegular, PersonLinkRegular } from '@fluentui/react-icons'
import OffspringDialog, { AcknowledgementMap, CustodyMap, MainstayMap } from './Dialog/OffspringDialog'
import { FieldArrayRenderProps } from 'formik/dist/FieldArray'
import { formatFullNumericDate } from '../../../../helpers/dateHelper'
import { Gender, PersonNameDto } from '../../../../config/contactConfig'
import { genderOptions } from './Dialog/Body'
import { FamilyLawAccordionItemProps, FamilyLawDialogAction } from '../../../../pages/File/Registration/FamilyLawOverview'
import PersonAvatar from '../../../PersonAvatar'

type ChildItemProps = FamilyLawAccordionItemProps &
    FieldArrayRenderProps & {
        index: number
        offspring: OffspringDto
        onClick: () => void
    }

const GenderMap: Map<Gender, string> = new Map<Gender, string>(genderOptions.map(gender => [gender.key, gender.text]))

export const getFullName = (name: PersonNameDto) => {
    return name.given.concat(' ', name.insertion ? name.insertion + ' ' : '', name.last)
}

function ChildItem(props: ChildItemProps): ReactElement {
    const description = `${GenderMap.get(props.offspring.child.gender)} | ${formatFullNumericDate(
        props.offspring.child.dateOfBirth,
    )} | BSN: ${props.offspring.child.identificationNumber}`

    return (
        <TreeItem itemType={'leaf'}>
            <TreeItemPersonaLayout
                description={description}
                onClick={() => props.onClick()}
                media={<PersonAvatar />}
                actions={
                    <Button
                        size="small"
                        appearance="transparent"
                        icon={<DeleteRegular />}
                        onClick={(event: React.MouseEvent) => {
                            event.stopPropagation()
                            props.remove(props.index)
                        }}
                    />
                }
            >
                <div className={mergeClasses(props.styles.offspringNameMain, props.useColumns && props.styles.offspringNameGrid)}>
                    <div>{getFullName(props.offspring.child.name)}</div>
                    <div className={mergeClasses(props.styles.offspringInfo, props.useColumns && props.styles.alignColumnEnd)}>
                        {<HomeRegular />} {MainstayMap.get(props.offspring.mainstay)} {<AccessibilityRegular />}
                        {CustodyMap.get(props.offspring.custody)} {<PersonLinkRegular />} {AcknowledgementMap.get(props.offspring.acknowledgement)}
                    </div>
                </div>
            </TreeItemPersonaLayout>
        </TreeItem>
    )
}

export function Offspring(props: FamilyLawAccordionItemProps): ReactElement {
    const { values, setErrors } = useFormikContext<FamilyLawModuleDto>()
    const [mutateOffspring, setMutateOffspring] = useState(false)
    const [action, setAction] = useState<FamilyLawDialogAction>(undefined)
    const [index, setIndex] = useState<number>()

    return (
        <div>
            <AccordionHeader>Kinderen</AccordionHeader>
            <AccordionPanel>
                <Tree aria-label={'tree'}>
                    <div>
                        {values?.offspring?.length > 0 ? (
                            <FieldArray name={'offspring'}>
                                {arrayHelpers => {
                                    return values.offspring.map((child, index) => (
                                        <ChildItem
                                            {...props}
                                            key={`offpsring_${index}`}
                                            index={index}
                                            offspring={child}
                                            {...arrayHelpers}
                                            onClick={() => {
                                                setAction(FamilyLawDialogAction.Edit)
                                                setIndex(index)
                                                setMutateOffspring(true)
                                            }}
                                        />
                                    ))
                                }}
                            </FieldArray>
                        ) : (
                            <TreeItem itemType={'leaf'}>
                                <Text italic size={200}>
                                    Er zijn geen kinderen toegevoegd.
                                </Text>
                            </TreeItem>
                        )}
                    </div>
                    <Button
                        size="small"
                        appearance="transparent"
                        icon={<AddCircleRegular />}
                        onClick={() => {
                            setErrors({})
                            setAction(FamilyLawDialogAction.Add)
                            setMutateOffspring(true)
                        }}
                    >
                        Kind toevoegen
                    </Button>
                </Tree>
                {mutateOffspring && (
                    <FieldArray name={'offspring'}>
                        {arrayHelpers => (
                            <OffspringDialog
                                {...props}
                                open={mutateOffspring}
                                setOpen={setMutateOffspring}
                                action={action}
                                index={index}
                                {...arrayHelpers}
                            />
                        )}
                    </FieldArray>
                )}
            </AccordionPanel>
        </div>
    )
}
